// chenglog.jsx
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, List, Typography, Divider, Spin, Alert, Card, Statistic, Row, Col, Tag } from 'antd';
import { useSocketContext } from '../../sockets/SocketContext';
import { setDbActualDates } from '../../store/interfaceSlice';
import { formatDbActualDate, formatWipoDatesDisplay } from '../../utils/helpers';

const { Title, Text } = Typography;

/** Версия из package.json на момент сборки (craco DefinePlugin → __APP_VERSION__) */
/* global __APP_VERSION__ */
const APP_VERSION = __APP_VERSION__;

const changes = [
  {
    category: 'Добавлено',
    items: [
      'На узких экранах переключение режимов поиска ТЗ стрелками в панели вкладок.',
      'На мобильных устройствах главное меню открывается из шапки (кнопка «гамбургер») в боковой панели.',
      'Выбор класса МКТУ: полное описание класса в модальном окне по иконке «i»; в списке на телефоне — краткое превью вместо длинного текста.',
    ],
  },
  {
    category: 'Изменено',
    items: [
      'Адаптивная вёрстка шапки, футера, бокового меню и липких панелей поиска; стили вынесены в SASS/CSS.',
      'Компактный режим липкой панели поиска: без декоративных миниатюр в свёрнутом виде (название, описание, изображение).',
      'Таблица избранного: горизонтальный скролл, компактные карточки превью на узком экране, белый фон под областью прокрутки.',
      'Выпадающий список классов МКТУ: позиционирование и z-index, чтобы не перекрывался футером и панелями.',
      'Ant Design: всплывающие подсказки пунктов бокового меню рендерятся в document.body, чтобы не «залипали» при overflow.',
      'Карточки похожих предложений в анализе: аккуратнее горизонтальная прокрутка превью.',
      'Зависимости в package.json: убраны неиспользуемые @testing-library/*; старый Babel-плагин proposal заменён на transform.',
    ],
  },
];

// Русские подписи и иконки для статистики
const STAT_LABELS = {
  total_records: { label: 'Всего ТЗ', icon: '📦' },
  active_records: { label: 'Активных ТЗ', icon: '✅' },
  inactive_records: { label: 'Неактивных ТЗ', icon: '🗃️' },
  min_year: { label: 'Минимальный год', icon: '⏳' },
  max_year: { label: 'Максимальный год', icon: '⏰' },
  data_import_date: { label: 'Дата актуализации БД', icon: '📅' },
};

function formatNumber(num) {
  if (typeof num === 'string' && !isNaN(Number(num))) num = Number(num);
  if (typeof num === 'number') return num.toLocaleString('ru-RU');
  return num;
}

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const Chenglog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { socket } = useSocketContext();
  const statistics = useSelector(state => state.interface.csvStatistics);
  const dbActualDates = useSelector(state => state.interface.dbActualDates);
  const loading = open && !statistics;
  const datesLoading = open && !dbActualDates;
  const error = null;

  useEffect(() => {
    if (!open || !socket) return;
    const requestDates = () => {
      socket.emit('get_db_actual_dates', {}, (response) => {
        if (response?.status === 'ok' && response?.dates && typeof response.dates === 'object') {
          dispatch(setDbActualDates(response.dates));
        }
      });
    };
    if (socket.connected) requestDates();
    else socket.once('connect', requestDates);
  }, [open, socket, dispatch]);

  return (
    <Modal
      title={<span style={{ fontWeight: 700, fontSize: 22 }}>История изменений проекта</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
    >
      {/* Даты актуализации */}
      <Card size="small" className="theme-changelog-banner" style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>Даты актуализации данных</Title>
        {datesLoading ? (
          <Spin tip="Загрузка дат…" size="small" />
        ) : (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <Tag color="blue" style={{ margin: 0, padding: '6px 12px', fontSize: 13 }}>
                  📦 Товарные знаки: {formatDbActualDate(dbActualDates?.tmRfDate) || '—'}
                </Tag>
              </Col>
              <Col xs={24} sm={8}>
                <Tag color="green" style={{ margin: 0, padding: '6px 12px', fontSize: 13 }}>
                  📋 ВИПО: {formatWipoDatesDisplay(dbActualDates?.wipoDates)}
                </Tag>
              </Col>
              <Col xs={24} sm={8}>
                <Tag color="orange" style={{ margin: 0, padding: '6px 12px', fontSize: 13 }}>
                  📄 Заявки: {formatDbActualDate(dbActualDates?.appRfDate) || '—'}
                </Tag>
              </Col>
            </Row>
          </>
        )}
      </Card>
      <Divider style={{ margin: '16px 0' }} />
      {/* Статистика */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 8 }}>Статистика</Title>
        {loading ? (
          <Spin tip="Загрузка статистики..." />
        ) : error ? (
          <Alert type="error" message={error} showIcon />
        ) : statistics ? (
          <Card bordered className="theme-success-surface">
            <Row gutter={[16, 16]}>
              {Object.entries(statistics).map(([key, value]) => {
                const meta = STAT_LABELS[key] || { label: key, icon: '' };
                let displayValue = value;
                if (key === 'data_import_date') displayValue = formatDateTime(value);
                else displayValue = formatNumber(value);
                return (
                  <Col span={12} key={key}>
                    <Statistic
                      title={<span>{meta.icon} {meta.label}</span>}
                      value={displayValue}
                    />
                  </Col>
                );
              })}
            </Row>
          </Card>
        ) : (
          <Text type="secondary">Нет данных</Text>
        )}
      </div>
      <Divider />
      {/* Версия приложения */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 0 }}>Версия приложения</Title>
        <Text strong>v{APP_VERSION}</Text>
      </div>
      <Divider />
      {/* Изменения */}
      <div>
        <Title level={4} style={{ marginBottom: 8 }}>Список изменений</Title>
        {changes.map((changeCategory, index) => (
          <div key={index} style={{ marginBottom: '10px' }}>
            <Title level={5}>{changeCategory.category}</Title>
            <List
              dataSource={changeCategory.items}
              renderItem={item => (
                <List.Item>
                  <Text>{item}</Text>
                </List.Item>
              )}
            />
            {index < changes.length - 1 && <Divider style={{ margin: '8px 0' }} />}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default Chenglog;
